import { ReviewScore } from "../../model/datasource/score";
import { max } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import {
	ScaleBand, scaleBand, ScaleLinear, scaleLinear, ScaleOrdinal, scaleOrdinal
} from "d3-scale";
import { select, Selection } from "d3-selection";
import { Series, SeriesPoint, stack } from "d3-shape";

interface Dimensions {
	width: number;
	height: number;
	padding: {
		top: number;
		left: number;
		bottom: number;
		right: number;
	};
	totalWidth: number;
	totalHeight: number;
}

type InnerDatum = {
	section: string;
	correct: number;
	incorrect: number;
	unanswered: number;
	total: number;
};
// `& { key?: string }` allows us to add key information to each series segment
type Datum = SeriesPoint<InnerDatum> & { key?: string };
type DatumSeries = Series<InnerDatum, string>;
type Chart = Selection<SVGElement, unknown, HTMLElement, any>;
type Tooltip = Selection<HTMLElement, unknown, HTMLElement, any>;
type Segments = Selection<any, any, any, any>;
type Fill = Selection<any, any, any, any>;

interface Axes {
	x: ScaleLinear<number, number>;
	y: ScaleBand<string>;
	background: ScaleOrdinal<string, string>;
	foreground: ScaleOrdinal<string, string>;
	title: ScaleOrdinal<string, string>;
}

export default class ReviewSectionScoreChart {
	heightMult: number;
	width: number;
	padTop: number;
	padLeft: number;
	padBottom: number;
	padRight: number;
	categories: string[];
	colors: string[];

	constructor(categories: string[] | null, colors: string[] | null, width: number, heightMult: number, padTop: number, padLeft: number, padBottom: number, padRight: number) {
		this.heightMult = heightMult || 36;
		this.width = width || 200;
		this.padTop = padTop || 0;
		this.padLeft = padLeft || 120;
		this.padBottom = padBottom || 20;
		this.padRight = padRight || 5;

		this.categories = categories || ['correct', 'incorrect', 'unanswered'];
		this.colors = colors || ['#28a745', '#dc3545', '#17a2b8'];
	}

	display(element: HTMLElement, sections: ReviewScore[]): void {

		const dimensions: Dimensions = this.getDimensions(
			this.width, this.heightMult * sections.length,
			this.padTop, this.padLeft, this.padBottom, this.padRight
		);

		const sectionData = this.formatData(sections, this.categories);
		const axes =
			this.axes(sections, this.categories, this.colors, dimensions);

		const chart = this.addChart(element.querySelector<HTMLElement>('svg'), dimensions);
		const tooltip = this.addTooltip(element);
		const bars = this.addBars(chart, axes.background, sectionData);

		this.fillBars(bars, axes.x, axes.y);
		this.addEvents(bars, tooltip, element, axes.title);
		this.addAxes(chart, dimensions.height, axes.x, axes.y, 18);
	}

	getDimensions(width: number, height: number, padTop: number, padLeft: number, padBottom: number, padRight: number): Dimensions {
		return {
			width: width,
			height: height,
			padding: { top: padTop, left: padLeft, bottom: padBottom, right: padRight },
			totalWidth: width + padLeft + padRight,
			totalHeight: height + padTop + padBottom
		};
	}

	formatData(sections: ReviewScore[], categories: string[]): DatumSeries[] {
		return stack<InnerDatum>()
			.keys(categories)
			(sections.map(s => ({
				section: s.section,
				correct: s.correct,
				incorrect: s.incorrect,
				unanswered: s.unanswered,
				total: s.total
			})));
	}

	axes(sections: ReviewScore[], categories: string[], colors: string[], dimensions: Dimensions): Axes {
		return {
			x: scaleLinear()
				.domain([0, max(sections, d => d.total)])
				.range([0, dimensions.width]),
			y: scaleBand()
				.domain(sections.map(s => s.section))
				.range([0, dimensions.height])
				.padding(0.12),
			background: scaleOrdinal<string>()
				.domain(categories)
				.range(colors),
			foreground: scaleOrdinal<string>()
				.domain(categories)
				.range(['white', 'white', 'white']),
			title: scaleOrdinal<string>()
				.domain(categories)
				.range(['Correct', 'Incorrect', 'Unanswered'])
		};
	}

	addChart(element: HTMLElement, dimensions: Dimensions): Chart {
		const svg = select(element)
			.attr('width', dimensions.totalWidth)
			.attr('height', dimensions.totalHeight);
		return svg.append('g')
			.attr('transform', 'translate(' +
				dimensions.padding.left + ',' +
				dimensions.padding.top +
				')')
			.attr('width', dimensions.width)
			.attr('height', dimensions.height);
	}

	addTooltip(parent: HTMLElement): Tooltip {
		return select(parent)
			.insert('div', ':first-child')
				.style('float', 'left')
				.style('width', 0)
			.insert('div', ':first-child')
				.attr('class', 'border rounded p-1 bg-light')
				.style('width', 'max-content')
				.style('display', 'none')
				.style('position', 'relative');
	}

	addBars(chart: Chart, background: ScaleOrdinal<string, string>, data: DatumSeries[]): Segments {
		return chart.selectAll('g')
			.data(data)
			.join('g')
			.attr('fill', d => background(d.key))
			.selectAll('g')
			.data(d => d.map((i: Datum) => {
				i.key = d.key;
				return i;
			}))
			.join('g');
	}

	addEvents(bars: Segments, tooltip: Tooltip, parent: HTMLElement, title: ScaleOrdinal<string, string>): Segments {
		return bars.on("mouseover", (event: MouseEvent, data: Datum) => {
			tooltip.style('display', 'block');
			select(event.currentTarget as Element)
				.select("rect")
				.style("opacity", 0.95);
		})
			.on("mouseleave", (event: MouseEvent, data: Datum) => {
				tooltip.style('display', 'none');
				select(event.currentTarget as Element)
					.select("rect")
					.style("opacity", 1);
			})
			.on("mousemove", (event: MouseEvent, data: Datum) => {
				const svgRect = parent.getBoundingClientRect();
				tooltip.style('top', (
					event.pageY - svgRect.y - window.scrollY
				) + 'px')
					.style('left', (
						event.pageX - svgRect.x - window.scrollX + 20
					) + 'px')
					.html('<b>'+data.data.section+'</b><br/>'+
						title(data.key)+': ' +
						data.data[data.key] + ' / ' + data.data.total
					);
			});
	}

	fillBars(bars: Segments, x: ScaleLinear<number, number>, y: ScaleBand<string>): Fill {
		return bars.append("rect")
			.attr("x", d => x(d[0]))
			.attr("y", d => y(d.data.section))
			.attr("width", d => x(d[1]) - x(d[0]))
			.attr("height", y.bandwidth());
	}

	addAxes(chart: Chart, height: number, x: ScaleLinear<number, number>, y: ScaleBand<string>, truncate: number): void {
		chart.append("g")
			.attr("transform", "translate(0, "+height+")")
			.call(axisBottom(x));
		chart.append("g")
			.attr("transform", "translate(0, 0)")
			.call(axisLeft(y)
				.tickFormat(d => d.length > truncate ?
					d.substring(0, truncate) + "...":
					d
				)
			);
	}
}